import { faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Room } from '@spike/room-model';
import clsx from 'clsx';
import { Autocomplete, Spinner } from 'components/UI';
import Button from 'components/UI/Button';
import { MobileDivider } from 'components/UI/MobileDivider';
import { FunctionComponent, useEffect, useState } from 'react';
import Section from '../UI/Section';
import StaffChipRoom from './UI/StaffChipRoom';

interface RoomsProps {
    isEditing: boolean;
    allRooms: Array<Room>;
    selectedRoomIds: Array<number>;
    onChange: (roomIds: Array<number>) => void;
    className?: string;
    isRoomsLoading: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        fullContainer: {
            width: '100%',
            height: '100%'
        },
        container: {
            gap: 12,
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            padding: '0px 16px',

            [theme.breakpoints.up('md')]: {
                gap: 16,
                paddingLeft: '38px',
                paddingTop: '30px',
                paddingRight: '38px',
                paddingBottom: '30px'
            },
            [theme.breakpoints.up('xl')]: {
                paddingLeft: '50px',
                paddingTop: '40px',
                paddingRight: '50px',
                paddingBottom: '40px'
            }
        },
        title: {
            [theme.breakpoints.down('lg')]: {
                fontSize: 20,
                fontWeight: 600
            }
        },
        addedRoom: {
            maxWidth: '100%'
        },
        chip: {
            '& span': {}
        },
        button: {
            height: 40,
            fontWeight: 100,
            border: '1px solid #92B4A7',
            color: '#92B4A7',

            [theme.breakpoints.down('lg')]: {
                'color': '#fff',
                'padding': '0px 16px',
                'backgroundColor': '#92B4A7',

                '& span': {
                    fontSize: 14,
                    fontWeight: 600
                }
            },
            [theme.breakpoints.up('xl')]: {
                height: '40px',
                marginBottom: '28px'
            }
        },
        plusButton: {
            width: 37,
            height: 37,
            cursor: 'pointer'
        },
        searchRooms: {
            display: 'flex',
            flexDirection: 'column-reverse',
            [theme.breakpoints.down('lg')]: {
                height: '30px'
            },
            [theme.breakpoints.up('xl')]: {
                height: '40px'
            }
        },
        roomsMessageHeader: {
            fontWeight: 500,
            [theme.breakpoints.down('lg')]: {
                fontSize: '15px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '20px'
            }
        },
        roomsMessageBody: {
            color: '#B7B7B7',
            [theme.breakpoints.down('lg')]: {
                fontSize: '12px',
                marginBottom: 15
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '16px'
            }
        },
        autocomplete: {
            [theme.breakpoints.down('lg')]: {
                width: '100%',
                marginBottom: 15
            }
        }
    })
);

export const Rooms: FunctionComponent<RoomsProps> = props => {
    const classes = useStyles();

    const [selectedRooms, setSelectedRooms] = useState<Array<Room>>(
        props.allRooms.filter(room => props.selectedRoomIds.includes(room.id ?? 0))
    );

    const [searchResult, setSearchResult] = useState<Array<Room>>(
        props.allRooms.filter(room => !props.selectedRoomIds.includes(room.id ?? 0))
    );

    const [searchText, setSearchText] = useState('');

    const [isAutoSelected, setIsAutoSelected] = useState(false);

    const [showRoomList, setShowRoomList] = useState(false);

    useEffect(() => {
        setSearchResult(
            searchText.length === 0
                ? props.allRooms.filter(room => !props.selectedRoomIds.includes(room.id ?? 0))
                : props.allRooms
                      .filter(room => !props.selectedRoomIds.includes(room.id ?? 0))
                      .filter(service => {
                          const name = `${service.name.toLowerCase()}`;
                          return name.indexOf(searchText.toLowerCase()) > -1;
                      })
        );
    }, [searchText, props.selectedRoomIds]);

    useEffect(() => {
        setSearchResult(props.allRooms.filter(room => !props.selectedRoomIds.includes(room.id ?? 0)));
    }, [props.allRooms]);

    useEffect(() => {
        props.onChange(selectedRooms.map(room => room.id ?? 0));
    }, [selectedRooms]);

    useEffect(() => {
        if (!isAutoSelected && props.allRooms.length > 0 && !props.isEditing) {
            setSelectedRooms(props.allRooms);
            setIsAutoSelected(true);
        }
    }, [props.allRooms]);

    const changeSearchTextHanlder = (searchText: string) => {
        setSearchText(searchText);
    };

    const openRoomsListHandler = () => {
        setShowRoomList(true);
    };

    const selectRoomHandler = (selectedRoom: Room): void => {
        setSelectedRooms(prevSelectedRooms => {
            return [...prevSelectedRooms, selectedRoom];
        });

        setShowRoomList(false);
        setSearchText('');
    };

    const deleteHandler = (deletedRoom: Room) => {
        setSelectedRooms(prevSelectedRooms => {
            return prevSelectedRooms.filter(room => room.id !== deletedRoom.id);
        });
    };

    const clickAwayHandler = () => {
        setSearchText('');
        setShowRoomList(false);
    };

    const addRoomsButton = (
        <Button
            id="rooms_button_add_service"
            color="green"
            variant="secondary"
            className={classes.button}
            onClick={openRoomsListHandler}
            label="+ Add Room"
        />
    );

    const plusButton = (
        <FontAwesomeIcon
            icon={faPlusCircle}
            color="#92B4A7"
            size="2x"
            onClick={openRoomsListHandler}
            className={classes.plusButton}
        />
    );

    const noRoomMessage = (
        <Box>
            <Typography className={classes.roomsMessageHeader}>No rooms have been created yet.</Typography>
            <Typography className={classes.roomsMessageBody}>
                You can come back to add rooms once you create them.
            </Typography>
        </Box>
    );

    const serviceList = (
        <Autocomplete
            id="room_filter_search_by_name"
            options={searchResult
                .sort((service, otherService) => {
                    return service.name.localeCompare(otherService.name);
                })
                .map(service => ({ id: service.id!, name: service.name }))}
            placeholder="Search by name"
            emptyOptionsText="Rooms not found"
            emptySearhcBoxShowAllOptions={true}
            className={classes.autocomplete}
            onClickAway={clickAwayHandler}
            onChangeSearchText={changeSearchTextHanlder}
            onSelect={option => {
                const room = props.allRooms.find(s => s.id === option.id)!;
                selectRoomHandler(room);
            }}
        />
    );

    return (
        <Box className={clsx(classes.fullContainer, props.className)}>
            <MobileDivider />

            <Section
                title="Rooms & Spaces"
                editable={true}
                collapsed={false}
                titleClassName={classes.title}
            >
                <Box className={classes.container}>
                    {props.isRoomsLoading ? (
                        <Box>
                            <Spinner />
                        </Box>
                    ) : (
                        <>
                            {props.allRooms.length === 0 ? (
                                noRoomMessage
                            ) : (
                                <>
                                    {showRoomList ? (
                                        serviceList
                                    ) : (
                                        <>
                                            {selectedRooms
                                                .sort((room, otherRoom) =>
                                                    room.name.toLowerCase().localeCompare(otherRoom.name.toLowerCase())
                                                )
                                                .map(room => (
                                                    <Box
                                                        key={room.uuid}
                                                        className={classes.addedRoom}
                                                    >
                                                        <StaffChipRoom
                                                            small={false}
                                                            room={room}
                                                            className={classes.chip}
                                                            onDelete={() => deleteHandler(room)}
                                                        />
                                                    </Box>
                                                ))}

                                            {selectedRooms.length > 0 ? plusButton : addRoomsButton}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Section>
        </Box>
    );
};

export default Rooms;
